import { useState, FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCreateCard, useUpdateCard, useArchiveCard, type Card, type CreateCardData, type UpdateCardData } from '../lib/cards'

interface CardFormProps {
  open: boolean
  onClose: () => void
  card?: Card | null
}

const CARD_TYPES = [
  { value: 'credit', label: 'Crédito' },
  { value: 'debit', label: 'Débito' },
  { value: 'prepaid', label: 'Pré-pago' },
] as const

const COLOR_OPTIONS = [
  { value: '#0C7DD8', label: 'Azul' },
  { value: '#28A745', label: 'Verde' },
  { value: '#DC3545', label: 'Vermelho' },
  { value: '#FFC107', label: 'Amarelo' },
  { value: '#6F42C1', label: 'Roxo' },
  { value: '#17A2B8', label: 'Teal' },
  { value: '#343A40', label: 'Cinza' },
] as const

export default function CardForm({ open, onClose, card }: CardFormProps) {
  const [name, setName] = useState(card?.name ?? '')
  const [cardType, setCardType] = useState<'credit' | 'debit' | 'prepaid'>(card?.card_type ?? 'credit')
  const [color, setColor] = useState<string>(card?.color ?? '')

  const createCard = useCreateCard()
  const updateCard = useUpdateCard()
  const archiveCard = useArchiveCard()
  const queryClient = useQueryClient()

  const isEditing = !!card
  const isSubmitting = createCard.isPending || updateCard.isPending

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    
    if (!name.trim()) return

    const cardData: CreateCardData | UpdateCardData = {
      name: name.trim(),
      card_type: cardType,
      color: color || null,
    }

    if (isEditing && card) {
      updateCard.mutate({ id: card.id, updates: cardData as UpdateCardData }, {
        onSuccess: () => {
          onClose()
        },
      })
    } else {
      createCard.mutate(cardData as CreateCardData, {
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  const handleDelete = () => {
    if (card && window.confirm('Deseja realmente excluir este cartão? Esta ação não pode ser desfeita.')) {
      archiveCard.mutate(card.id, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['cards'] })
          onClose()
        },
      })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-[var(--color-surface)] rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-text)]">
            {isEditing ? 'Editar cartão' : 'Novo cartão'}
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-[var(--color-surface-subtle)] transition-colors"
          >
            <svg xmlns="http://www.w3.org/svg" className="h-5 w-5 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M18 12a.75.75 0 01-.75.75H12v4.75a.75.75 0 01-1.5 0V12.75H4.75a.75.75 0 010-1.5h4.75V6.25a.75.75 0 011.5 0v4.75h4.75a.75.75 0 01.75.75z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        <div className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Nome do cartão
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Cartão Principal"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Tipo
              </label>
              <select
                value={cardType}
                onChange={(e) => setCardType(e.target.value as typeof cardType)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)]"
              >
                {CARD_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Cor (opcional)
              </label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className={`h-8 w-8 rounded-full border-2 transition-all ${
                      color === c.value ? 'border-[var(--color-brand-500)] scale-110' : 'border-[var(--color-border)]'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => setColor('')}
                  className={`h-8 w-8 rounded-full border-2 flex items-center justify-center transition-all ${
                    !color ? 'border-[var(--color-brand-500)] bg-[var(--color-surface)]' : 'border-[var(--color-border)]'
                  }`}
                  title="Sem cor"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)] transition-colors"
              >
                Cancelar
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded-lg border border-[var(--color-danger)] bg-[var(--color-surface)] px-4 py-2 text-sm font-medium text-[var(--color-danger)] hover:bg-[var(--color-danger-light)] transition-colors"
                >
                  Excluir
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting || !name.trim()}
                className="rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-brand-600)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
